import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useNavigate } from 'react-router-dom';
import { getCn01FormsStatus, type Cn01FormStatusItem } from '~/apis/cn01PlanterApi';
import { getApiErrorMessage } from '~/apis/cn02FormApi';
import { useCurrentUserProfile } from '~/hooks/useCurrentUserProfile';
import {
  buildPermissionSet,
  FINANCIAL_PLANTER_CALCULATE_PERMISSIONS,
  hasAnyPermission,
  PEST_DISEASE_PLANTER_ACCESS_PERMISSIONS,
} from '~/lib/permissions';
import { ROUTES } from '~/utils/constants';
import { Cn01ApplicationStatusCard } from './components/Cn01ApplicationStatusCard';

export function PlanterDashboard() {
  const navigate = useNavigate();
  const { data: profile } = useCurrentUserProfile();
  const [items, setItems] = useState<Cn01FormStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const permissionSet = useMemo(
    () => buildPermissionSet(profile?.permissions),
    [profile?.permissions]
  );
  const canViewPestDisease = hasAnyPermission(
    permissionSet,
    PEST_DISEASE_PLANTER_ACCESS_PERMISSIONS
  );
  const canCalculateFinancial = hasAnyPermission(
    permissionSet,
    FINANCIAL_PLANTER_CALCULATE_PERMISSIONS
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getCn01FormsStatus()
      .then((res) => {
        if (!cancelled) setItems(res.items ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'Could not load application status.'));
          setItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const greetingName = profile?.fullName?.trim() || profile?.username?.trim() || 'Applicant';

  return (
    <Box sx={{ width: '100%', maxWidth: 960 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Welcome, {greetingName}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
          {canViewPestDisease ? (
            <Button
              variant="contained"
              startIcon={<BugReportIcon />}
              onClick={() => navigate(ROUTES.PEST_DISEASE)}
              sx={{ borderRadius: 2, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              Pest & Disease
            </Button>
          ) : null}
          {canCalculateFinancial ? (
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={() => navigate(ROUTES.FINANCIAL_ESTIMATE)}
              sx={{ borderRadius: 2, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              Cost Estimation
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track your CN01 application progress below.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <Alert severity="info">No CN01 applications found.</Alert>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <Stack spacing={3}>
          {items.map((item) => (
            <Cn01ApplicationStatusCard key={item.refNo} item={item} />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
